import React, { useEffect, useMemo, useState, useCallback } from "react";
import Tabs from "../../../components/Tabs/Tabs";
import Residuals from "./residuals";
import Members from "./members";
import Webhook from "./webhook";
import SubOrganizations from "./sub-organizations";
import OverRides from "./overrides";
import Equipments from "./equipments";
import styles from "./styles.module.scss";
import useCommonStore from "../../../stores/common";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import PlaceholderLogoIcon from "../../../assets/svg/icons/common/placeholder-logo";
import Avatar from "../../../components/Avatar/Avatar";
import PhoneIcon from "../../../assets/svg/icons/common/phone";
import MailIcon from "../../../assets/svg/icons/common/mail";
import LocationPinIcon from "../../../assets/svg/icons/common/location-pin";
import StatusDot from "../../../components/StatusDot/StatusDot";
import DropDown from "../../../components/DropDown/DropDown";

import {
  GetAdminUsers,
  getDepartmentsList,
  getEmsOffices,
  GetOrganizationById,
  getZohoList,
} from "../../../../api";
import useOrganizationStore from "../../../stores/organization";
import useUserStore from "../../../stores/user";
import Spinner from "../../../components/Loaders/Spinner";
import useSubOrganizations from "./sub-organizations/hooks/useSubOrganizations";
import config from "../../../../config";

const MainAdmin = () => {
  const mainAdminId = useOrganizationStore(
    (state) => state?.selectedOrganizationData?.organization?.main_admin,
  );
  const users = useOrganizationStore(
    (state) => state?.selectedOrganizationData?.users,
  );

  if (!mainAdminId) {
    return null;
  }

  const mainAdmin = users?.find((user) => user.id === mainAdminId);

  if (!mainAdmin) {
    return null;
  }

  return (
    <ul>
      <li>
        <span className={styles.listHeadTitle}>Admin</span>
        <span className={styles.listHeadValue}>
          <Avatar size="xs" name={mainAdmin?.name?.charAt(0)} />{" "}
          {mainAdmin?.name}
        </span>
      </li>
      <li>
        <PhoneIcon /> {mainAdmin.phone_direct}
      </li>
      <li>
        <MailIcon /> {mainAdmin.email}
      </li>
    </ul>
  );
};

const TeamLead = () => {
  const primaryContactId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.primary_contact_id,
  );
  const adminUsers = useOrganizationStore((state) => state.adminUsers);

  if (!primaryContactId) {
    return null;
  }

  if (!adminUsers) {
    return null;
  }

  const teamLead = adminUsers?.find((user) => user.id === primaryContactId);

  if (!teamLead) {
    return null;
  }

  return (
    <ul>
      <li>
        <span className={styles.listHeadTitle}>Team Lead</span>
        <span className={styles.listHeadValue}>
          <Avatar size="xs" name={teamLead?.name?.charAt(0)} /> {teamLead?.name}
        </span>
      </li>
      <li>
        <PhoneIcon /> {teamLead.phone_direct}
      </li>
      <li>
        <MailIcon /> {teamLead.email}
      </li>
    </ul>
  );
};

const Details = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const [src, setSrc] = useState("/img/organization/logo-solid.png");
  const [loading, setLoading] = useState(true);
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const toggleExpandDetails = useCommonStore(
    (state) => state.toggleExpandDetails,
  );
  const setEMSOffices = useOrganizationStore((state) => state.setEMSOffices);
  const setAdminUsers = useOrganizationStore((state) => state.setAdminUsers);
  const setZohoUsers = useOrganizationStore((state) => state.setZohoUsers);
  const setDepartments = useOrganizationStore((state) => state.setDepartments);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );

  const toggleShowDetails = useCommonStore((state) => state.toggleShowDetails);

  const setEditOrganization = useOrganizationStore(
    (state) => state?.setEditOrganization,
  );
  const setAddOrganizationModalOpen = useOrganizationStore(
    (state) => state.setAddOrganizationModalOpen,
  );

  const drop_down_options = [
    {
      text: "Edit Organization",
      type: "normal",
      onClick: () => {
        setEditOrganization(true);
        setAddOrganizationModalOpen(true);
      },
    },
  ];

  const onCloseClick = () => {
    toggleShowDetails();

    if (expandDetails) {
      toggleExpandDetails();
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await GetOrganizationById(selectedOrganizationId);
      if (!response?.success) {
        console.error("Error fetching organization details");
        console.error(response);
        return;
      }

      setSrc(`${config.paymentcloud.STRATUS_URL}/img/${response?.organization?.whitelabel_slug}/logo-solid.png`);

      setSelectedOrganizationData(response);
      setLoading(false);
    } catch (error) {
      console.error(
        "An error occurred while fetching organization details",
        error,
      );
    }
  }, [selectedOrganizationId, setSelectedOrganizationData]);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const response = await GetAdminUsers();

      if (!response?.success) {
        console.error("Error fetching admin users");
        console.error(response);
        return;
      }

      setAdminUsers(response?.users);
    } catch (error) {
      console.error("An error occurred while fetching admin users", error);
    }
  }, [selectedOrganizationId]);

  const fetchEMSOffices = useCallback(async () => {
    try {
      const response = await getEmsOffices();

      if (!response?.status) {
        console.error("Error fetching EMS offices");
        console.error(response);
        return;
      }

      setEMSOffices(response?.data);
    } catch (error) {
      console.error("An error occurred while fetching EMS offices", error);
    }
  }, [selectedOrganizationId]);

  const fetchZohoUsers = useCallback(async () => {
    try {
      const response = await getZohoList();

      if (!response?.success) {
        console.error("Error fetching Zoho users");
        console.error(response);
        return;
      }

      setZohoUsers(
        response?.zoho_list.map((user) => ({
          label: user.name,
          value: user.id,
        })),
      );
    } catch (error) {
      console.error("An error occurred while fetching Zoho users", error);
    }
  }, [setZohoUsers]);

  const fetchDepartmentList = useCallback(async () => {
    try {
      const response = await getDepartmentsList();

      if (!response?.status) {
        console.error("Error fetching department list");
        console.error(response);
        return;
      }

      setDepartments(
        response?.department_list.map((dept) => ({
          label: dept.name,
          value: dept.id,
        })),
      );
    } catch (error) {
      console.error("An error occurred while fetching department list", error);
    }
  }, [setDepartments]);

  const tabs = useMemo(
    () => [
      {
        label: "Residuals",
        content: <Residuals />,
        badge: null,
      },
      {
        label: "Members",
        content: <Members />,
        badge: String(selectedOrganizationData?.users?.length),
        badgeStyle: "normal",
      },
      {
        label: "Webhook",
        content: <Webhook />,
        badge: null,
      },
      {
        label: "Sub Organizations",
        content: <SubOrganizations />,
        badge: null,
      },
      {
        label: "Overrides",
        content: <OverRides />,
        badge: null,
        hidden: !isPCAdmin,
      },
      {
        label: "Equipments",
        content: <Equipments />,
        badge: null,
        hidden: !isPCAdmin,
      },
    ],
    [selectedOrganizationData, isPCAdmin],
  );

  // ToDo: implement abort controller or not set data if unmounted
  useEffect(() => {
    fetchData();
    fetchAdminUsers();
    fetchEMSOffices();
    fetchZohoUsers();
    fetchDepartmentList();
  }, []);

  useEffect(() => {
    fetchData();
    fetchAdminUsers();
    fetchEMSOffices();
    fetchZohoUsers();
    fetchDepartmentList();
  }, [selectedOrganizationId]);

  if (loading) {
    return (
      <div className={styles.details_container}>
        <div className={styles.loader_section}>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.details}>
      <div className={styles.details_head}>
        <button
          className={styles.expand_button}
          onClick={() => {
            toggleExpandDetails();
          }}
        >
          {!expandDetails ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96452 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM14.0306 15.2194C14.1003 15.2891 14.1556 15.3718 14.1933 15.4628C14.231 15.5539 14.2504 15.6515 14.2504 15.75C14.2504 15.8485 14.231 15.9461 14.1933 16.0372C14.1556 16.1282 14.1003 16.2109 14.0306 16.2806C13.9609 16.3503 13.8782 16.4056 13.7872 16.4433C13.6961 16.481 13.5986 16.5004 13.5 16.5004C13.4015 16.5004 13.3039 16.481 13.2128 16.4433C13.1218 16.4056 13.0391 16.3503 12.9694 16.2806L9.21938 12.5306C9.14965 12.461 9.09433 12.3783 9.05658 12.2872C9.01884 12.1962 8.99941 12.0986 8.99941 12C8.99941 11.9014 9.01884 11.8038 9.05658 11.7128C9.09433 11.6217 9.14965 11.539 9.21938 11.4694L12.9694 7.71937C13.0391 7.64969 13.1218 7.59442 13.2128 7.5567C13.3039 7.51899 13.4015 7.49958 13.5 7.49958C13.5986 7.49958 13.6961 7.51899 13.7872 7.5567C13.8782 7.59442 13.9609 7.64969 14.0306 7.71937C14.1003 7.78906 14.1556 7.87178 14.1933 7.96283C14.231 8.05387 14.2504 8.15145 14.2504 8.25C14.2504 8.34855 14.231 8.44613 14.1933 8.53717C14.1556 8.62822 14.1003 8.71094 14.0306 8.78063L10.8103 12L14.0306 15.2194Z"
                fill="#254FE2"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96452 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM14.7806 12.5306L11.0306 16.2806C10.9609 16.3503 10.8782 16.4056 10.7872 16.4433C10.6961 16.481 10.5986 16.5004 10.5 16.5004C10.4015 16.5004 10.3039 16.481 10.2128 16.4433C10.1218 16.4056 10.0391 16.3503 9.96938 16.2806C9.8997 16.2109 9.84442 16.1282 9.80671 16.0372C9.769 15.9461 9.74959 15.8485 9.74959 15.75C9.74959 15.6515 9.769 15.5539 9.80671 15.4628C9.84442 15.3718 9.8997 15.2891 9.96938 15.2194L13.1897 12L9.96938 8.78063C9.82865 8.63989 9.74959 8.44902 9.74959 8.25C9.74959 8.05098 9.82865 7.86011 9.96938 7.71937C10.1101 7.57864 10.301 7.49958 10.5 7.49958C10.699 7.49958 10.8899 7.57864 11.0306 7.71937L14.7806 11.4694C14.8504 11.539 14.9057 11.6217 14.9434 11.7128C14.9812 11.8038 15.0006 11.9014 15.0006 12C15.0006 12.0986 14.9812 12.1962 14.9434 12.2872C14.9057 12.3783 14.8504 12.461 14.7806 12.5306Z"
                fill="#254FE2"
              />
            </svg>
          )}

          <span>{!expandDetails ? "Expand Details" : "Minimize"}</span>
        </button>

        <ButtonIcon onClick={onCloseClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19.2806 18.2194C19.3503 18.289 19.4056 18.3718 19.4433 18.4628C19.481 18.5539 19.5004 18.6514 19.5004 18.75C19.5004 18.8485 19.481 18.9461 19.4433 19.0372C19.4056 19.1282 19.3503 19.2109 19.2806 19.2806C19.2109 19.3503 19.1282 19.4056 19.0372 19.4433C18.9461 19.481 18.8485 19.5004 18.75 19.5004C18.6515 19.5004 18.5539 19.481 18.4628 19.4433C18.3718 19.4056 18.2891 19.3503 18.2194 19.2806L12 13.0603L5.78062 19.2806C5.63989 19.4213 5.44902 19.5004 5.25 19.5004C5.05097 19.5004 4.8601 19.4213 4.71937 19.2806C4.57864 19.1399 4.49958 18.949 4.49958 18.75C4.49958 18.551 4.57864 18.3601 4.71937 18.2194L10.9397 12L4.71937 5.78061C4.57864 5.63988 4.49958 5.44901 4.49958 5.24999C4.49958 5.05097 4.57864 4.8601 4.71937 4.71936C4.8601 4.57863 5.05097 4.49957 5.25 4.49957C5.44902 4.49957 5.63989 4.57863 5.78062 4.71936L12 10.9397L18.2194 4.71936C18.3601 4.57863 18.551 4.49957 18.75 4.49957C18.949 4.49957 19.1399 4.57863 19.2806 4.71936C19.4214 4.8601 19.5004 5.05097 19.5004 5.24999C19.5004 5.44901 19.4214 5.63988 19.2806 5.78061L13.0603 12L19.2806 18.2194Z"
              fill="#A4A3A8"
            />
          </svg>
        </ButtonIcon>
      </div>

      <div className={styles.organizationInfo}>
        <div className={styles.orgInfoTop}>
          <div className={styles.orgLogoArea}>
            <div className={styles.orgLogoImageContainer}>
              {src ? (
                <img src={src} onError={() => setSrc("")} />
              ) : (
                <PlaceholderLogoIcon />
              )}
              <div className={styles.shadow}></div>
            </div>
            <div className={styles.orgNameContainer}>
              <p className={styles.orgName}>
                {selectedOrganizationData?.organization?.name}
              </p>
              <p className={styles.orgOpenSince}>
                <StatusDot color="#37B450" /> <span>Open since 31/12/2020</span>
              </p>
            </div>
          </div>

          <div className={styles.orgActions}>
            <DropDown options={drop_down_options}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M10.5 5.625C10.5 5.32833 10.588 5.03832 10.7528 4.79165C10.9176 4.54497 11.1519 4.35271 11.426 4.23918C11.7001 4.12565 12.0017 4.09594 12.2926 4.15382C12.5836 4.2117 12.8509 4.35456 13.0607 4.56434C13.2704 4.77412 13.4133 5.04139 13.4712 5.33237C13.5291 5.62334 13.4994 5.92494 13.3858 6.19903C13.2723 6.47311 13.08 6.70738 12.8334 6.87221C12.5867 7.03703 12.2967 7.125 12 7.125C11.6022 7.125 11.2206 6.96697 10.9393 6.68566C10.658 6.40436 10.5 6.02283 10.5 5.625ZM12 10.5C11.7033 10.5 11.4133 10.588 11.1666 10.7528C10.92 10.9176 10.7277 11.1519 10.6142 11.426C10.5006 11.7001 10.4709 12.0017 10.5288 12.2926C10.5867 12.5836 10.7296 12.8509 10.9393 13.0607C11.1491 13.2704 11.4164 13.4133 11.7074 13.4712C11.9983 13.5291 12.2999 13.4994 12.574 13.3858C12.8481 13.2723 13.0824 13.08 13.2472 12.8334C13.412 12.5867 13.5 12.2967 13.5 12C13.5 11.6022 13.342 11.2206 13.0607 10.9393C12.7794 10.658 12.3978 10.5 12 10.5ZM12 16.875C11.7033 16.875 11.4133 16.963 11.1666 17.1278C10.92 17.2926 10.7277 17.5269 10.6142 17.801C10.5006 18.0751 10.4709 18.3767 10.5288 18.6676C10.5867 18.9586 10.7296 19.2259 10.9393 19.4357C11.1491 19.6454 11.4164 19.7883 11.7074 19.8462C11.9983 19.9041 12.2999 19.8744 12.574 19.7608C12.8481 19.6473 13.0824 19.455 13.2472 19.2084C13.412 18.9617 13.5 18.6717 13.5 18.375C13.5 17.9772 13.342 17.5956 13.0607 17.3143C12.7794 17.033 12.3978 16.875 12 16.875Z"
                  fill="#3A383F"
                />
              </svg>
            </DropDown>

          </div>
        </div>

        <div
          className={`${styles.orgContactInfo} ${expandDetails ? styles.expanded : ""}`}
        >
          <ul>
            <li>
              <span className={styles.listHeadTitle}>Organization ID</span>
              <span className={styles.listHeadValue}>
                {selectedOrganizationData?.organization?.id}
              </span>
            </li>
            <li>
              <PhoneIcon /> {selectedOrganizationData?.organization?.phone}
            </li>
            <li>
              <MailIcon /> {selectedOrganizationData?.organization?.email}
            </li>
            <li>
              <LocationPinIcon />{" "}
              {selectedOrganizationData?.organization?.address}
            </li>
          </ul>

          {expandDetails && (
            <>
              <MainAdmin />
              <TeamLead />
            </>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} active_index={0} />
    </div>
  );
};

export default Details;
